import type { ReactNode } from 'react';
import { cn } from '#client/lib/cn';

interface EmptyStateProps {
  icon?: ReactNode | undefined;
  title: string;
  body?: ReactNode | undefined;
  /** A button or link offering the obvious next step, where there is one. */
  action?: ReactNode | undefined;
  /**
   * Reads as good news rather than as absence — "All caught up" instead of an
   * empty queue. Used wherever a count of zero is a *positive* result.
   */
  tone?: 'neutral' | 'positive' | undefined;
  className?: string | undefined;
}

/**
 * A real empty state: icon, heading, one line of explanation, and a way
 * forward.
 *
 * A bare string ("No panels yet") tells the user nothing about why the screen is empty or what to do about it.
 */
export function EmptyState({
  icon,
  title,
  body,
  action,
  tone = 'neutral',
  className,
}: EmptyStateProps) {
  return (
    <div className={cn('c-empty', tone === 'positive' && 'c-empty--positive', className)}>
      {icon && (
        <span className="c-empty__icon" aria-hidden="true">
          {icon}
        </span>
      )}
      <p className="c-empty__title">{title}</p>
      {body && <p className="c-empty__body">{body}</p>}
      {action && <div className="c-empty__action">{action}</div>}
    </div>
  );
}
